import { ReactFlowProvider } from "@xyflow/react";
import { type ReactNode, useEffect } from "react";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import type { ArchitectureData } from "@/lib/arch-parser";
import { type ArchVizExtras, ArchVizProvider, useArchViz } from "./ArchVizContext";
import { ArchVizDialogs } from "./ArchVizDialogs";
import { DetailPanel } from "./DetailPanel";
import { DropZone } from "./DropZone";
import { FlowPlayer } from "./FlowPlayer";
import { GraphCanvas } from "./GraphCanvas";
import { LeftRail } from "./LeftRail";
import { RisksSheet } from "./RisksSheet";
import { TopBar } from "./TopBar";

interface ArchVizAppProps {
  /** When provided, viz renders from this data instead of the local drop zone. */
  architecture?: ArchitectureData | null;
  /** Slot rendered above the canvas — solution header, version dropdown, etc. */
  header?: ReactNode;
  /** Optional element rendered into the right side of the canvas top bar. */
  topBarSlot?: ReactNode;
  /** Set true to hide the built-in top bar entirely (used for shared views). */
  hideTopBar?: boolean;
  /** Set true to hide the close-file button on the top bar (no LOAD/CLEAR cycle). */
  readOnly?: boolean;
  /** Set false to suppress the TopBar's ThemeToggle when an outer header already shows one. */
  showThemeToggle?: boolean;
  /** Extras threaded into context — version id, comment counts, current user. */
  extras?: Partial<ArchVizExtras>;
}

function VizShell({
  architecture,
  header,
  topBarSlot,
  hideTopBar,
  readOnly,
  showThemeToggle,
}: ArchVizAppProps) {
  const { state, dispatch } = useArchViz();
  useKeyboardShortcuts();

  // When an external architecture is passed in, sync it into the reducer.
  useEffect(() => {
    if (architecture && architecture !== state.architecture) {
      dispatch({ type: "LOAD", architecture });
    }
    // Intentionally do NOT clear when architecture becomes null —
    // unmount handles teardown. Switching solutions remounts this tree.
  }, [architecture, dispatch, state.architecture]);

  if (!state.architecture) return <DropZone />;

  const scaleValue = state.labelScale === "small" ? 0.85 : state.labelScale === "large" ? 1.4 : 1;

  return (
    <ReactFlowProvider>
      <div className="flex flex-col h-screen overflow-hidden">
        {!state.fullscreen && header}
        {/* When a page header is supplied it renders the breadcrumb/search
            cluster itself (one bar). The built-in TopBar is only for the
            local route, which has no outer header. */}
        {!hideTopBar && !state.fullscreen && !header && (
          <TopBar topBarSlot={topBarSlot} readOnly={readOnly} showThemeToggle={showThemeToggle} />
        )}
        <div
          className="flex flex-1 min-h-0"
          style={{ "--archviz-label-scale": scaleValue } as React.CSSProperties}
        >
          {!state.fullscreen && <LeftRail readOnly={readOnly} />}
          <div className="flex-1 relative min-w-0">
            <GraphCanvas />
            <FlowPlayer />
          </div>
          <DetailPanel />
          <RisksSheet />
        </div>
        <ArchVizDialogs />
      </div>
    </ReactFlowProvider>
  );
}

export function ArchVizApp(props: ArchVizAppProps = {}) {
  return (
    <ArchVizProvider extras={props.extras}>
      <VizShell {...props} />
    </ArchVizProvider>
  );
}
